import React, {
    type HTMLProps,
    type ReactElement,
    type ReactNode,
    useEffect,
    useState
} from 'react';

import classnames from 'classnames';

import {
    Button,
    type ButtonProps,
    Icon,
    IconButton,
    type IconButtonProps,
    type IconName,
    type IconProps,
    Label,
    Popover,
    type PopoverProps,
    Text,
    Tooltip,
    type TooltipProps
} from '../..';
import useTranslation from '../../features/locale/useTranslation';

import styles from './ActionMenu.module.scss';

export type ActionMenuProps = {
    actions: ActionMenuItem[];
    className?: string;
    children?: ReactNode;
    title?: string;
    placement?: PopoverProps['placement'];
    onOpenChange?: (open: boolean) => void;
    inline?: boolean;
    getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
    open?: boolean;
    slotProps?: {
        trigger?: Partial<IconButtonProps>;
        tooltip?: Partial<TooltipProps>;
        triggerIcon?: Partial<IconProps>;
    };
} & HTMLProps<HTMLDivElement>;

export type ActionMenuItem = {
    label: string;
    icon?: IconName;
    isDestructive?: boolean;
    tooltip?: Omit<TooltipProps, 'children'>;
    render?: (button: ReactElement) => ReactElement;
} & Omit<ButtonProps, 'children' | 'icon'>;

export const ActionMenu = ({
    actions,
    className,
    children,
    title,
    placement,
    onOpenChange,
    inline,
    getPopupContainer,
    slotProps,
    open = false,
    ...rest
}: ActionMenuProps): ReactElement => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(open);

    const handleOpenChange = (value: boolean) => {
        setIsOpen(value);
        onOpenChange?.(value);
    };

    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    const actionsOptions = (
        <div {...rest} className={styles.container}>
            {title && <Label className={styles.title}>{title}</Label>}
            {actions.map((action) => {
                const {
                    label,
                    className: actionClassName,
                    id,
                    icon,
                    tooltip,
                    onClick,
                    onMouseDown,
                    isDestructive,
                    size,
                    color,
                    render,
                    ...buttonProps
                } = action;

                const handleClick = (event: never) => {
                    handleOpenChange(false);
                    onClick?.(event);
                };

                const handleMouseDown: typeof onMouseDown = (event: never) => {
                    if (onMouseDown) {
                        handleOpenChange(false);
                        onMouseDown(event);
                    }
                };

                const button = (
                    <Button
                        id={id}
                        {...buttonProps}
                        className={classnames(actionClassName, styles.actionItem)}
                        color={color ?? (isDestructive ? 'error' : 'primary')}
                        onClick={handleClick}
                        onMouseDown={handleMouseDown}
                        type="ghost"
                        size={size ?? 'medium'}
                        shape="square"
                        fill
                        slotProps={{ content: { className: styles.actionItemContent } }}
                    >
                        {icon && <Icon name={icon} size={size ?? 'medium'} />}
                        <Text compact truncate>
                            {label}
                        </Text>
                    </Button>
                );
                const buttonWithRender = render ? render(button) : button;
                if (!tooltip) {
                    return <React.Fragment key={label}>{buttonWithRender}</React.Fragment>;
                }
                return (
                    <Tooltip key={label} {...tooltip}>
                        {buttonWithRender}
                    </Tooltip>
                );
            })}
        </div>
    );

    if (inline) {
        return <div className={className}>{actionsOptions}</div>;
    }

    return (
        <Popover
            className={className}
            placement={placement ?? 'bottomRight'}
            content={actionsOptions}
            trigger="click"
            open={isOpen}
            onOpenChange={handleOpenChange}
            getPopupContainer={getPopupContainer ?? ((trigger) => trigger.parentElement)}
            noPadding
            noArrow
        >
            {children || (
                <Tooltip
                    title={t('More actions')}
                    placement="top"
                    mouseEnterDelay={500}
                    {...slotProps?.tooltip}
                >
                    <IconButton
                        type="ghost"
                        aria-haspopup="true"
                        aria-expanded={isOpen}
                        className={classnames(styles.trigger, {
                            [styles.active]: isOpen
                        })}
                        {...slotProps?.trigger}
                        icon={<Icon name="s-icon-ellipsis-vertical" {...slotProps?.triggerIcon} />}
                    />
                </Tooltip>
            )}
        </Popover>
    );
};

ActionMenu.displayName = 'ActionMenu';
